/**
 * Load every feature that can be shown in the vector attribute table.
 *
 * GeoJSON / OGC Features endpoints are preferred (complete property bag).
 * PMTiles archives are decoded at a bounded zoom so a modest admin layer
 * (districts, subdistricts) fills the table without downloading the world.
 */
import Feature from 'ol/Feature.js';
import MVT from 'ol/format/MVT.js';
import type Geometry from 'ol/geom/Geometry.js';
import { PMTiles } from 'pmtiles';
import type { Layer } from '@geosoft/viewer-sdk';
import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import {
  readPmtilesArchiveInfo,
  resolvePmtilesSourceLayerFromInfo,
} from '@/engines/pmtiles/sourceLayer';
import {
  buildNativePmtilesTilePlan,
  mercatorTileExtent,
} from '@/engines/giro3d/nativePmtilesVectorLayer';
import {
  featuresFromGeoJsonPayload,
  withCoordinateColumns,
} from './vectorAttributeRows';
import { mergeVectorTileFeatureFragments } from './mergeVectorTileFeatureFragments';

const DEFAULT_ATTRIBUTE_ZOOM = 10;
const DEFAULT_MAX_TILES = 256;

export async function loadVectorAttributeFeatures(
  layer: Layer,
): Promise<ReadonlyArray<VectorExportFeature>> {
  const featuresUrl =
    typeof layer.endpoints.features === 'string' && layer.endpoints.features.length > 0
      ? layer.endpoints.features
      : null;
  if (featuresUrl) {
    const fromJson = await loadFromFeaturesUrl(featuresUrl);
    if (fromJson.length > 0) return fromJson;
  }

  const pmtilesUrl =
    typeof layer.endpoints.pmtiles === 'string' && layer.endpoints.pmtiles.length > 0
      ? layer.endpoints.pmtiles
      : null;
  if (pmtilesUrl) {
    return loadFromPmtiles(layer, pmtilesUrl);
  }

  return [];
}

async function loadFromFeaturesUrl(url: string): Promise<VectorExportFeature[]> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Attribute fetch failed (HTTP ${response.status})`);
  }
  return featuresFromGeoJsonPayload(await response.json());
}

async function loadFromPmtiles(
  layer: Layer,
  url: string,
): Promise<VectorExportFeature[]> {
  const info = await readPmtilesArchiveInfo(url);
  const sourceLayer = resolvePmtilesSourceLayerFromInfo(layer, info);
  if (!sourceLayer.applied) {
    throw new Error(`PMTiles source-layer could not be resolved for ${layer.id}`);
  }
  const bounds = info.header?.bounds ?? plausibleLonLatBounds(layer.bbox);
  if (!bounds) {
    throw new Error(`PMTiles archive bounds are unavailable for ${layer.id}`);
  }

  const plan = buildNativePmtilesTilePlan({
    bounds,
    minZoom: info.header?.minZoom ?? 0,
    maxZoom: info.header?.maxZoom ?? 14,
    preferredZoom: readNumber(layer.style, 'attributeTableZoom')
      ?? readNumber(layer.style, 'vectorTileZoom')
      ?? DEFAULT_ATTRIBUTE_ZOOM,
    maxTileCount: readNumber(layer.style, 'attributeTableMaxTiles') ?? DEFAULT_MAX_TILES,
  });

  const archive = new PMTiles(url);
  const format = new MVT<Feature<Geometry>>({
    featureClass: Feature,
    layers: [sourceLayer.applied],
  });
  const rows: VectorExportFeature[] = [];

  for (let x = plan.minX; x <= plan.maxX; x += 1) {
    for (let y = plan.minY; y <= plan.maxY; y += 1) {
      const tile = await archive.getZxy(plan.zoom, x, y);
      if (!tile?.data) continue;
      const features = format.readFeatures(tile.data, {
        extent: mercatorTileExtent(plan.zoom, x, y),
        featureProjection: 'EPSG:3857',
      });
      for (const feature of features) {
        rows.push(rowFromOlFeature(feature));
      }
    }
  }

  return mergeVectorTileFeatureFragments(rows).map(withCoordinateColumns);
}

function rowFromOlFeature(feature: Feature<Geometry>): VectorExportFeature {
  const raw = (feature.getProperties?.() ?? {}) as Record<string, unknown>;
  const properties: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (key === 'geometry' || typeof value === 'object') continue;
    properties[key] = value;
  }
  const id = feature.getId?.() ?? properties.OBJECTID_1 ?? properties.OBJECTID ?? properties.id;
  return {
    id: typeof id === 'string' || typeof id === 'number' ? id : undefined,
    properties,
    geometry: olGeometryToGeoJson(feature.getGeometry?.()),
  };
}

function olGeometryToGeoJson(geometry: Geometry | undefined): unknown {
  if (!geometry || typeof geometry.getType !== 'function') return undefined;
  const type = geometry.getType();
  const coordinates = (geometry as Geometry & { getCoordinates?: () => unknown }).getCoordinates?.();
  if (coordinates === undefined) return undefined;
  return { type, coordinates };
}

function plausibleLonLatBounds(
  bbox: readonly number[] | undefined,
): readonly [number, number, number, number] | null {
  if (!Array.isArray(bbox) || bbox.length < 4) return null;
  const [minLon, minLat, maxLon, maxLat] = bbox;
  if (![minLon, minLat, maxLon, maxLat].every(Number.isFinite)) return null;
  if (minLon < -180 || maxLon > 180 || minLat < -90 || maxLat > 90 || maxLon <= minLon || maxLat <= minLat) {
    return null;
  }
  return [minLon, minLat, maxLon, maxLat];
}

function readNumber(style: Record<string, unknown> | undefined, key: string): number | null {
  const value = style?.[key];
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}
